import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, X, Check, AlertCircle } from 'lucide-react';
import { useAuth } from '../../auth/AuthProvider';
import { useTranslation } from 'react-i18next';

const GradingScales = () => {
  const { api, user } = useAuth();
  const { t } = useTranslation('gradingscales');

  const [subjects, setSubjects] = useState([]);
  const [gradingScales, setGradingScales] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [filteredScales, setFilteredScales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingScale, setEditingScale] = useState(null);
  const [deletingScale, setDeletingScale] = useState(null);

  // Form states
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [gradeEntries, setGradeEntries] = useState([
    { grade_letter: '', minimum_score: '', maximum_score: '' }
  ]);

  // Check if current language is RTL
  const isRTL = localStorage.getItem('i18nextLng') === 'ar';

  // Fetch subjects and grading scales on component mount
  useEffect(() => {
    fetchSubjects();
    fetchGradingScales();
  }, []);

  // Set default subject and filter scales when data is loaded
  useEffect(() => {
    if (subjects.length > 0 && selectedSubject === '') {
      setSelectedSubject(subjects[0].id.toString());
    }
  }, [subjects]);

  // Filter scales when subject selection changes
  useEffect(() => {
    if (selectedSubject === '') {
      setFilteredScales([]);
    } else {
      setFilteredScales(gradingScales.filter(scale => scale.subject_id.toString() === selectedSubject));
    }
  }, [selectedSubject, gradingScales]);

  const fetchSubjects = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/v1/subjects');
      setSubjects(response.data);
    } catch (err) {
      handleError(err, t('errors.fetchSubjects'));
    } finally {
      setLoading(false);
    }
  };

  const fetchGradingScales = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/v1/grading-scales');
      setGradingScales(response.data);
    } catch (err) {
      handleError(err, t('errors.fetchGradingScales'));
    } finally {
      setLoading(false);
    }
  };

  const handleError = (err, defaultMessage) => {
    if (err.response?.data?.detail) {
      if (Array.isArray(err.response.data.detail)) {
        // Validation errors
        setError(err.response.data.detail.map(e => e.msg).join(', '));
      } else {
        // General error
        setError(err.response.data.detail);
      }
    } else {
      setError(defaultMessage);
    }
  };

  const resetForm = () => {
    setSelectedSubjects([]);
    setGradeEntries([{ grade_letter: '', minimum_score: '', maximum_score: '' }]);
    setError('');
  };

  const addGradeEntry = () => {
    setGradeEntries([...gradeEntries, { grade_letter: '', minimum_score: '', maximum_score: '' }]);
  };

  const removeGradeEntry = (index) => {
    if (gradeEntries.length > 1) {
      setGradeEntries(gradeEntries.filter((_, i) => i !== index));
    }
  };

  const updateGradeEntry = (index, field, value) => {
    const updated = [...gradeEntries];
    updated[index][field] = value;
    setGradeEntries(updated);
  };

  const handleSubjectSelection = (subjectId) => {
    setSelectedSubjects(prev => 
      prev.includes(subjectId) 
        ? prev.filter(id => id !== subjectId)
        : [...prev, subjectId]
    );
  };

  const createGradingScales = async () => {
    try {
      setLoading(true);
      setError('');

      if (selectedSubjects.length === 0) {
        setError(t('errors.selectSubject'));
        return;
      }

      if (gradeEntries.some(entry => !entry.grade_letter || !entry.minimum_score || !entry.maximum_score)) {
        setError(t('errors.fillGradeEntries'));
        return;
      }

      // Close modal first to show response
      setShowCreateModal(false);

      const promises = [];
      selectedSubjects.forEach(subjectId => {
        gradeEntries.forEach(entry => {
          promises.push(
            api.post('/api/v1/grading-scales', {
              grade_letter: entry.grade_letter,
              minimum_score: parseInt(entry.minimum_score),
              maximum_score: parseInt(entry.maximum_score),
              subject_id: subjectId,
              school_id: user.school_id
            })
          );
        });
      });

      await Promise.all(promises);
      await fetchGradingScales();
      resetForm();
    } catch (err) {
      handleError(err, t('errors.createGradingScales'));
    } finally {
      setLoading(false);
    }
  };

  const updateGradingScale = async () => {
    try {
      setLoading(true);
      setError('');

      // Close modal first to show response
      setShowEditModal(false);

      await api.put(`/api/v1/grading-scales/${editingScale.id}`, {
        grade_letter: editingScale.grade_letter,
        minimum_score: parseInt(editingScale.minimum_score),
        maximum_score: parseInt(editingScale.maximum_score),
        subject_id: editingScale.subject_id,
        school_id: user.school_id
      });

      await fetchGradingScales();
      setEditingScale(null);
    } catch (err) {
      handleError(err, t('errors.updateGradingScale'));
    } finally {
      setLoading(false);
    }
  };

  const deleteGradingScale = async () => {
    try {
      setLoading(true);
      setError('');

      await api.delete(`/api/v1/grading-scales/${deletingScale.id}`);
      await fetchGradingScales();
      setShowDeleteModal(false);
      setDeletingScale(null);
    } catch (err) {
      handleError(err, t('errors.deleteGradingScale'));
    } finally {
      setLoading(false);
    }
  };

  const getSubjectName = (subjectId) => {
    const subject = subjects.find(s => s.id === subjectId);
    return subject ? subject.subject_name : t('common.unknownSubject');
  };

  return (
    <div className={`min-h-screen bg-gray-50 ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">{t('title')}</h1>
              <p className="text-gray-600 mt-1">{t('subtitle')}</p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className={`flex items-center gap-2 bg-[#0E5252] text-white px-6 py-3 rounded-lg hover:bg-[#0a4040] transition-colors font-medium ${isRTL ? 'flex-row-reverse' : ''}`}
            >
              <Plus size={20} />
              {t('buttons.createGradingScale')}
            </button>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <AlertCircle className="text-red-500 mt-0.5" size={20} />
            <div className="text-red-700 flex-1">{error}</div>
            <button
              onClick={() => setError('')}
              className={`text-red-500 hover:text-red-700 ${isRTL ? 'mr-auto' : 'ml-auto'}`}
            >
              <X size={16} />
            </button>
          </div>
        )}

        {/* Subject Filter */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('filters.filterBySubject')}</h2>
          <select
            value={selectedSubject}
            onChange={(e) => setSelectedSubject(e.target.value)}
            className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0E5252] focus:border-transparent"
          >
            {subjects.map(subject => (
              <option key={subject.id} value={subject.id}>
                {subject.subject_name}
              </option>
            ))}
          </select>
        </div>

        {/* Grading Scales List */}
        <div className="bg-white border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0E5252]"></div>
            </div>
          ) : filteredScales.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-500 text-lg">{t('messages.noGradingScales')}</div>
              <p className="text-gray-400 mt-2">{t('messages.createFirstScale')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-200">
                <thead>
                  <tr className="bg-gray-50">
                    <th className={`px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider border-b whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                      {t('table.gradeLetter')}
                    </th>
                    <th className={`px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider border-b whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                      {t('table.scoreRange')}
                    </th>
                    <th className={`px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider border-b whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                      {t('table.subject')}
                    </th>
                    <th className={`px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider border-b whitespace-nowrap ${isRTL ? 'text-left' : 'text-right'}`}>
                      {t('table.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredScales.map((scale, index) => (
                    <tr key={scale.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                        <span className="inline-flex items-center justify-center w-10 h-10 bg-[#0E5252] text-white rounded-lg font-bold text-lg">
                          {scale.grade_letter}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                        <span className="font-medium">
                          {scale.minimum_score} - {scale.maximum_score}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                        <span className="px-3 py-1 bg-gray-100 rounded-full text-sm font-medium text-gray-700">
                          {getSubjectName(scale.subject_id)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                        <div className={`flex justify-end`}>
                          <button
                            onClick={() => {
                              setEditingScale(scale);
                              setShowEditModal(true);
                            }}
                            className="p-2 text-gray-600 hover:text-[#0E5252] hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => {
                              setDeletingScale(scale);
                              setShowDeleteModal(true);
                            }}
                            className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Create Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-bold text-gray-900">{t('modals.createTitle')}</h2>
                  <button
                    onClick={() => {
                      setShowCreateModal(false);
                      resetForm();
                    }}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X size={24} />
                  </button>
                </div>
              </div>

              <div className="p-6">
                {/* Subject Selection */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{t('modals.selectSubjects')}</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {subjects.map(subject => (
                      <label key={subject.id} className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
                        <input
                          type="checkbox"
                          checked={selectedSubjects.includes(subject.id)}
                          onChange={() => handleSubjectSelection(subject.id)}
                          className="w-4 h-4 text-[#0E5252] rounded focus:ring-[#0E5252]"
                        />
                        <span className="text-gray-900">{subject.subject_name}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Grade Entries */}
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-lg font-semibold text-gray-900">{t('modals.gradeLettersRanges')}</h3>
                    <button
                      onClick={addGradeEntry}
                      className="text-[#0E5252] hover:bg-[#0E5252] hover:text-white px-3 py-1 rounded-lg transition-colors"
                    >
                      + {t('buttons.addGrade')}
                    </button>
                  </div>

                  <div className="space-y-3">
                    {gradeEntries.map((entry, index) => (
                      <div key={index} className="flex flex-col sm:flex-row gap-3 p-4 border rounded-lg">
                        <div className="flex-1">
                          <label className={`block text-sm font-medium text-gray-700 mb-1 ${isRTL ? 'text-right' : ''}`}>
                            {t('forms.gradeLetter')}
                          </label>
                          <input
                            type="text"
                            value={entry.grade_letter}
                            onChange={(e) => updateGradeEntry(index, 'grade_letter', e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0E5252] focus:border-transparent"
                            placeholder={t('placeholders.gradeLetter')}
                          />
                        </div>
                        <div className="flex-1">
                          <label className={`block text-sm font-medium text-gray-700 mb-1 ${isRTL ? 'text-right' : ''}`}>
                            {t('forms.minimumScore')}
                          </label>
                          <input
                            type="number"
                            value={entry.minimum_score}
                            onChange={(e) => updateGradeEntry(index, 'minimum_score', e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0E5252] focus:border-transparent"
                            placeholder="70"
                            min="0"
                            max="100"
                          />
                        </div>
                        <div className="flex-1">
                          <label className={`block text-sm font-medium text-gray-700 mb-1 ${isRTL ? 'text-right' : ''}`}>
                            {t('forms.maximumScore')}
                          </label>
                          <input
                            type="number"
                            value={entry.maximum_score}
                            onChange={(e) => updateGradeEntry(index, 'maximum_score', e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0E5252] focus:border-transparent"
                            placeholder="79"
                            min="0"
                            max="100"
                          />
                        </div>
                        {gradeEntries.length > 1 && (
                          <button
                            onClick={() => removeGradeEntry(index)}
                            className="text-red-600 hover:text-red-800 p-2"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className={`flex gap-3 ${isRTL ? 'justify-start' : 'justify-end'}`}>
                  <button
                    onClick={() => {
                      setShowCreateModal(false);
                      resetForm();
                    }}
                    className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    {t('buttons.cancel')}
                  </button>
                  <button
                    onClick={createGradingScales}
                    disabled={loading}
                    className={`px-6 py-2 bg-[#0E5252] text-white rounded-lg hover:bg-[#0a4040] transition-colors disabled:opacity-50 flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <Check size={16} />
                    )}
                    {t('buttons.createScales')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {showEditModal && editingScale && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
              <div className="p-6 border-b">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-bold text-gray-900">{t('modals.editTitle')}</h2>
                  <button
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingScale(null);
                    }}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X size={24} />
                  </button>
                </div>
              </div>

              <div className="p-6">
                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium text-gray-700 mb-1 ${isRTL ? 'text-right' : ''}`}>
                      {t('forms.gradeLetter')}
                    </label>
                    <input
                      type="text"
                      value={editingScale.grade_letter}
                      onChange={(e) => setEditingScale({...editingScale, grade_letter: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0E5252] focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium text-gray-700 mb-1 ${isRTL ? 'text-right' : ''}`}>
                      {t('forms.minimumScore')}
                    </label>
                    <input
                      type="number"
                      value={editingScale.minimum_score}
                      onChange={(e) => setEditingScale({...editingScale, minimum_score: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0E5252] focus:border-transparent"
                      min="0"
                      max="100"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium text-gray-700 mb-1 ${isRTL ? 'text-right' : ''}`}>
                      {t('forms.maximumScore')}
                    </label>
                    <input
                      type="number"
                      value={editingScale.maximum_score}
                      onChange={(e) => setEditingScale({...editingScale, maximum_score: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0E5252] focus:border-transparent"
                      min="0"
                      max="100"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium text-gray-700 mb-1 ${isRTL ? 'text-right' : ''}`}>
                      {t('forms.subject')}
                    </label>
                    <select
                      value={editingScale.subject_id}
                      onChange={(e) => setEditingScale({...editingScale, subject_id: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0E5252] focus:border-transparent"
                    >
                      {subjects.map(subject => (
                        <option key={subject.id} value={subject.id}>
                          {subject.subject_name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className={`flex gap-3 mt-6 ${isRTL ? 'justify-start' : 'justify-end'}`}>
                  <button
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingScale(null);
                    }}
                    className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    {t('buttons.cancel')}
                  </button>
                  <button
                    onClick={updateGradingScale}
                    disabled={loading}
                    className={`px-6 py-2 bg-[#0E5252] text-white rounded-lg hover:bg-[#0a4040] transition-colors disabled:opacity-50 flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <Check size={16} />
                    )}
                    {t('buttons.update')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && deletingScale && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-md">
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-800">{t('modals.confirmDelete')}</h2>
              </div>

              <div className="p-4">
                <p className="text-gray-700 mb-4">
                  {t('modals.deleteConfirmation', {
                    gradeLetter: deletingScale.grade_letter,
                    minimumScore: deletingScale.minimum_score,
                    maximumScore: deletingScale.maximum_score,
                    subjectName: getSubjectName(deletingScale.subject_id)
                  })}
                </p>

                <div className={`flex mt-4 ${isRTL ? 'justify-start' : 'justify-end'}`}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowDeleteModal(false);
                      setDeletingScale(null);
                    }}
                    className={`px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 ${isRTL ? 'ml-2' : 'mr-2'}`}
                  >
                    {t('buttons.cancel')}
                  </button>
                  <button
                    type="button"
                    onClick={deleteGradingScale}
                    disabled={loading}
                    className={`px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md shadow-sm hover:bg-red-700 disabled:opacity-50 flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <Trash2 size={16} />
                    )}
                    {t('buttons.delete')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GradingScales;